// Private staff / service ratings — stored on this device only (mock).
// Nothing is transmitted. Only a staff *id* is kept (no names / personal data).
// The customer-facing API deliberately exposes just "already rated?" — never
// historical scores, averages or rankings.
import { scopedKey, readJSON, writeJSON, makeId } from "@/lib/storage";

const STAFF_KEY = "staff_ratings";
const SERVICE_KEY = "service_ratings";
const MAX_COMMENT = 500;

export const STAFF_CRITERIA = ["overall", "care", "speed", "courtesy"];
export const SERVICE_CRITERIA = ["speed", "care", "overall"];

function validScores(scores, criteria) {
  return criteria.every((c) => Number.isInteger(scores?.[c]) && scores[c] >= 1 && scores[c] <= 5);
}

function pickScores(scores, criteria) {
  return Object.fromEntries(criteria.map((c) => [c, scores[c]]));
}

function cleanComment(comment) {
  return String(comment ?? "").trim().slice(0, MAX_COMMENT);
}

// One staff rating per staff assignment per order (or per session without an order).
export function staffRatingTarget({ staffId, orderId, sessionId }) {
  return `${staffId}:${orderId ? `order:${orderId}` : `session:${sessionId}`}`;
}

// One general service rating per table session.
export function serviceRatingTarget({ sessionId }) {
  return `session:${sessionId}`;
}

function hasTarget(name, target) {
  return readJSON(scopedKey(name), []).some((r) => r.target === target);
}

function save(name, record) {
  const list = readJSON(scopedKey(name), []);
  if (list.some((r) => r.target === record.target)) return { ok: false, reason: "duplicate" };
  // Only a rating that actually reached storage counts as submitted/rated.
  if (!writeJSON(scopedKey(name), [...list, record])) return { ok: false, reason: "storage" };
  return { ok: true };
}

export function submitStaffRating({ staffId, orderId, sessionId, branchId, scores, comment, now = Date.now() }) {
  if (!staffId || !validScores(scores, STAFF_CRITERIA)) return { ok: false, reason: "invalid" };
  return save(STAFF_KEY, {
    id: makeId("srt"),
    target: staffRatingTarget({ staffId, orderId, sessionId }),
    staffId,
    orderId: orderId || null,
    sessionId: sessionId || null,
    branchId: branchId || null,
    scores: pickScores(scores, STAFF_CRITERIA),
    comment: cleanComment(comment),
    createdAt: now,
  });
}

export function submitServiceRating({ sessionId, orderId, branchId, scores, comment, now = Date.now() }) {
  if (!sessionId || !validScores(scores, SERVICE_CRITERIA)) return { ok: false, reason: "invalid" };
  return save(SERVICE_KEY, {
    id: makeId("svr"),
    target: serviceRatingTarget({ sessionId }),
    sessionId,
    orderId: orderId || null,
    branchId: branchId || null,
    scores: pickScores(scores, SERVICE_CRITERIA),
    comment: cleanComment(comment),
    createdAt: now,
  });
}

// Customer-facing status only: { alreadyRated }. No scores are returned.
export function getStaffRatingStatus(target) {
  return { alreadyRated: hasTarget(STAFF_KEY, target) };
}

export function getServiceRatingStatus(target) {
  return { alreadyRated: hasTarget(SERVICE_KEY, target) };
}
